import React from 'react';
import { ClientInvitation } from '../../../../../hooks/api/useMyOrganizationClientInvitations';
import { ProfileListItem } from '../../../../../primary-record-types/apiTypes/medicalProfileService';
import { getIntl } from '../../../../../utils/intl';
import { findInviteProfile } from '../utils';
import Stack, { contentStackSpacing } from '../../../../v2BaseComponents/Stack';
import { Switch } from '../../../../v2BaseComponents/Inputs';
import SelectExistingProfile from './SelectExistingProfile';
import { ProfileResponse } from '../../types';
import CreateProfile from './CreateProfile';
import StepPaper from '../StepPaper';
import MainContentContainer from '../../../../v2BaseComponents/MainContentContainer';

const intl = getIntl('organization-client-invitation-medical-profile-step', {
    selectExistingProfile: 'Select an existing profile',
    paperTitle: 'Confirm Client Medical Profile',
});

export type MedicalProfileStepOwnProps = {
    clientInvitation: ClientInvitation;
    profiles: ProfileListItem[];
    onProfileSubmit: (response: ProfileResponse) => void;
};

export type MedicalProfileStepProps = MedicalProfileStepOwnProps;

const MedicalProfileStep: React.FC<MedicalProfileStepProps> = (props) => {
    const selectExistingProfileDisabled = props.profiles.length === 0;
    const matchingExistingProfileId =
        findInviteProfile(props.profiles, props.clientInvitation)?.medicalProfileId || null;
    const [selectExistingProfile, setSelectExistingProfile] = React.useState<boolean>(
        !!matchingExistingProfileId && !selectExistingProfileDisabled,
    );

    return (
        <StepPaper stepIndex={0} stepTitle={intl('paperTitle')}>
            <MainContentContainer>
                <Stack vertical fullWidth space={contentStackSpacing} align="center">
                    {!selectExistingProfileDisabled ? (
                        <Switch
                            title={intl('selectExistingProfile')}
                            value={selectExistingProfile}
                            onChange={setSelectExistingProfile}
                            testID="organization-invitation-select-existing-profile-switch"
                        />
                    ) : null}
                    {selectExistingProfile && !selectExistingProfileDisabled ? (
                        <SelectExistingProfile
                            clientInvitation={props.clientInvitation}
                            profiles={props.profiles}
                            defaultProfileId={matchingExistingProfileId}
                            onProfileSelected={(profileId) => {
                                props.onProfileSubmit({
                                    type: 'existing',
                                    profileId,
                                });
                            }}
                        />
                    ) : (
                        <CreateProfile
                            clientInvitation={props.clientInvitation}
                            onSubmit={(profileInfo) =>
                                props.onProfileSubmit({
                                    type: 'new',
                                    profileInfo,
                                })
                            }
                        />
                    )}
                </Stack>
            </MainContentContainer>
        </StepPaper>
    );
};

export default React.memo(MedicalProfileStep);
